import { track } from "@amplitude/analytics-react-native";
import { useApolloClient, useFragment } from "@apollo/client";
import { useCallback, useRef, useState } from "react";
import { FORCE_FILTER_LOADING } from "src/api/featureFlags";
import GatheringForFilteredPhotosReadyFragmentFragmentDoc from "src/api/graphql/__generated__/documents/GatheringForFilteredPhotosReadyFragmentFragmentDoc";
import GetClientProfileForPhoneNumberDocument from "src/api/graphql/__generated__/documents/GetClientProfileForPhoneNumberDocument";
import LongPollGatheringForFilteredPhotosReadyDocument from "src/api/graphql/__generated__/documents/LongPollGatheringForFilteredPhotosReadyDocument";
import RequestGatheringSeedFacesSmsConfirmationDocument from "src/api/graphql/__generated__/documents/RequestGatheringSeedFacesSmsConfirmationDocument";
import { requestApiPin } from "src/api/graphql/middleware/multiRegionMiddleware/stores/useMultiRegionStore";
import { useGatheringStore } from "src/contexts/GatheringContext";
import useMountEffect from "src/hooks/useMountEffect";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";
import { alert } from "src/swsh-native";
import GlobalRefreshTopic from "src/topics/GlobalRefreshTopic";

const useIsFilterInitializing = () => {
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const setViewMode = useGatheringStore((state) => state.setViewMode);
	const [dismissed, setDismissed] = useState(false);
	const { data: cachedGathering } = useFragment({
		fragment: GatheringForFilteredPhotosReadyFragmentFragmentDoc,
		from: {
			__typename: "Gathering",
			gatheringId,
		},
	});
	const apolloClient = useApolloClient();
	const initialWaitingForPhotos = useRef(cachedGathering?.waitingForSelfFilteredPhotos ?? false);
	const [loading, setLoading] = useState(initialWaitingForPhotos.current);

	useMountEffect(() => {
		let didExit = false;
		(async () => {
			if (initialWaitingForPhotos.current) {
				const t = getCurTime_MS();
				const { data } = await apolloClient.query({
					query: LongPollGatheringForFilteredPhotosReadyDocument,
					variables: {
						input: {
							gatheringId,
						},
					},
					context: {
						pinBehavior: "long",
					},
					fetchPolicy: "network-only",
				});
				if (didExit) return;
				const didSucceed = data?.longPollGathering?.waitingForSelfFilteredPhotos === false;
				track("gathering_filterInitLongPoll", {
					gatheringId,
					success: didSucceed,
					duration: getCurTime_MS() - t,
				});

				if (
					// Success case
					didSucceed
				) {
					// Ensures that the request for filtered photos is routed to the primary region
					// Otherwise, they may not be populated yet
					requestApiPin();

					GlobalRefreshTopic.emit("refresh", {});
					setViewMode("Filter");
				}

				// We want to turn off the filter either way
				// The long poll times out after 30 seconds after which we should probably just give up
				setLoading(false);

				if (
					// Couldn't find case
					data?.longPollGathering?.waitingForSelfFilteredPhotos === true
				) {
					track("gathering_filterInitFail_timeout", {
						gatheringId,
					});
					const { data } = await apolloClient.query({
						query: GetClientProfileForPhoneNumberDocument,
					});
					if (didExit) return;
					const hasPhoneNumber = Boolean(data?.getClientProfile?.hasPhoneNumber);
					if (hasPhoneNumber) {
						alert({
							title: "You're in line!",
							message:
								"We're experiencing higher traffic than usual. Would you like us to text you once we've searched for your pics?",
							buttons: [
								{
									style: "cancel",
									text: "No thanks",
									onPress: () => {
										track("gathering_filterInitFail_skipSms", {
											gatheringId,
										});
									},
								},
								{
									text: "Yes!",
									isPreferred: true,
									style: "default",
									onPress: async () => {
										track("gathering_filterInitFail_sendSms", {
											gatheringId,
										});
										await apolloClient.mutate({
											mutation:
												RequestGatheringSeedFacesSmsConfirmationDocument,
											variables: {
												input: {
													gatheringId,
												},
											},
										});
									},
								},
							],
						});
					} else {
						alert({
							title: "You're in line!",
							message:
								"We're experiencing higher traffic than usual. Check back later to see if we've found any pics of you.",
							buttons: [
								{
									text: "Got it",
								},
							],
						});
					}
				}
			}
		})();
		return () => {
			didExit = true;
		};
	}, []);

	const forceDismiss = useCallback(() => {
		setDismissed(true);
	}, []);

	return {
		isFilterInitializing: loading,
		isFilterDismissed: dismissed,
		forceDismiss,
		showFilterLoading: !dismissed && (loading || FORCE_FILTER_LOADING),
	};
};
export default useIsFilterInitializing;
